import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { SupportService } from 'src/app/services/support.service';

@Component({
  selector: 'app-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss']
})
export class TicketListComponent implements OnInit, OnDestroy {
  tickets: any[] = [];
  stats: any = null;
  role: any = localStorage.getItem('role');
  loading = false;

  statusFilter = '';
  priorityFilter = '';
  categoryFilter = '';
  searchQuery = '';

  currentPage = 1;
  totalCount = 0;
  itemsPerPage = 20;

  private searchSubject = new Subject<string>();
  private searchSub!: Subscription;

  constructor(
    private supportService: SupportService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.role = localStorage.getItem('role');
    if (this.role === 'admin') {
      this.loadStats();
    }
    this.loadTickets();

    this.searchSub = this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
      this.currentPage = 1;
      this.loadTickets();
    });
  }

  ngOnDestroy() {
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  loadStats() {
    this.supportService.getTicketStats().subscribe(
      (res: any) => {
        if (res && res.response && res.response.data) {
          this.stats = res.response.data;
        }
      },
      (err: any) => {
        console.error('Failed to load ticket stats', err);
      }
    );
  }

  loadTickets() {
    this.loading = true;
    const params: any = {
      page: this.currentPage,
      limit: this.itemsPerPage
    };
    if (this.statusFilter) { params['status'] = this.statusFilter; }
    if (this.priorityFilter) { params['priority'] = this.priorityFilter; }
    if (this.categoryFilter) { params['category'] = this.categoryFilter; }
    if (this.searchQuery) { params['search'] = this.searchQuery; }

    this.supportService.getTickets(params).subscribe(
      (res: any) => {
        this.loading = false;
        if (res && res.response && res.response.data) {
          this.tickets = res.response.data.tickets || res.response.data || [];
          this.totalCount = res.response.data.total || res.response.data.count || 0;
        }
      },
      (err: any) => {
        this.loading = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to load tickets');
      }
    );
  }

  onFilterChange() {
    this.currentPage = 1;
    this.loadTickets();
  }

  onSearchChange() {
    this.searchSubject.next(this.searchQuery);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadTickets();
  }

  viewTicket(id: string) {
    this.router.navigate(['/support/tickets', id]);
  }

  createTicket() {
    this.router.navigate(['/support/tickets/new']);
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'open': return 'warning';
      case 'in_progress': return 'primary';
      case 'resolved': return 'success';
      case 'closed': return 'basic';
      default: return 'info';
    }
  }

  formatStatus(status: string): string {
    if (!status) { return ''; }
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'basic';
      default: return 'basic';
    }
  }
}
